import React, { useState, useEffect } from 'react';
import tw from 'twin.macro';
import styled from 'styled-components';
import AnimationRevealPage from 'helpers/AnimationRevealPage.js';
import { useSelector } from 'react-redux';
import { format, parse } from 'date-fns';
import { obtenerPedidosParaCocinera } from 'scripts/obtenerPedidosParaCocinera';
import { marcarPedidoComoTerminado } from 'scripts/marcarPedidoComoTerminado';
import { reasignarPedido } from 'scripts/reasignarPedido';
import { toast } from 'react-toastify';
import { actualizarPedidoCocinera } from 'scripts/actualizarPedidoCocinera';

// Styled Components
const Container = tw.div`p-6 bg-white rounded-lg w-full mx-auto mt-8`;
const PedidoItemBase = tw.div`flex justify-between items-center border-b pb-2 mb-2 p-4 rounded-lg shadow-md`;
const Button = tw.button`px-4 py-2 bg-green-500 text-white rounded hover:bg-green-700`;
const SectionTitle = tw.h3`text-lg font-bold cursor-pointer flex justify-between items-center`;
const Divider = tw.hr`my-2 border-t border-gray-300`;

const AssignedPedidoItem = styled(PedidoItemBase)`
	background-color: #99cdb6;
`;
const UnassignedAtrasoItem = styled(PedidoItemBase)`
	background-color: #ffc4c4;
`;
const UnassignedNormalItem = styled(PedidoItemBase)`
	background-color: #f0f0f0;
`;
const OtherCocineraItem = styled(PedidoItemBase)`
	background-color: #add8e6;
`;

// Helper Components
const ProductosList = ({ detalles, productoValido }) => (
	<div>
		{detalles
			?.filter((detalle) => productoValido(detalle))
			.map((detalle, index) => (
				<div key={index}>
					<p>Producto: {detalle.descripcion}</p>
					<p>Cantidad: {detalle.cantidad}</p>
					<Divider />
				</div>
			))}
	</div>
);

const PedidoInfo = ({ pedido, showFechaEntrega, showCocineraInfo }) => {
	const formatFecha = (fecha) => {
		if (!fecha) return 'Sin fecha definida';
		return format(parse(fecha, 'yyyy-MM-dd', new Date()), 'dd/MM/yyyy');
	};
	const notasCocinera = String(pedido?.notas_cocinera || '').trim();

	return (
		<>
			{showFechaEntrega && (
				<>
					<p>Fecha de entrega: {formatFecha(pedido.fecha_entrega)}</p>
				</>
			)}
			{showCocineraInfo && notasCocinera && (
				<>
					<p>Notas para la cocinera: {notasCocinera}</p>
				</>
			)}
		</>
	);
};

const PedidoButtons = ({ pedido, buttons, onMarcarTerminado, onReasignar, onVolverAPreparar }) => (
	<div>
		{buttons.includes('terminar') && (
			<Button onClick={() => onMarcarTerminado(pedido.id)}>Marcar como Terminado</Button>
		)}
		{buttons.includes('asignar') && <Button onClick={() => onReasignar(pedido.id)}>Preparar</Button>}
		{buttons.includes('reasignar') && <Button onClick={() => onReasignar(pedido.id)}>Reasignar</Button>}
		{buttons.includes('volverAPreparar') && (
			<Button onClick={() => onVolverAPreparar(pedido.id)}>Volver a preparar</Button>
		)}
	</div>
);

const PedidoCard = ({ pedido, StyledComponent, config, handlers, productoValido }) => {
	return (
		<StyledComponent key={pedido.id}>
			<div>
				<p>
					Pedido #{pedido.id}
					{pedido.cliente_nombre && ` - ${pedido.cliente_nombre}`}
				</p>
				<Divider />
				<ProductosList detalles={pedido.detalles} productoValido={productoValido} />
				<PedidoInfo
					pedido={pedido}
					showFechaEntrega={config.showFechaEntrega}
					showCocineraInfo={config.showCocineraInfo}
				/>
			</div>
			{config.buttons.length > 0 && (
				<PedidoButtons
					pedido={pedido}
					buttons={config.buttons}
					onMarcarTerminado={handlers.onMarcarTerminado}
					onReasignar={handlers.onReasignar}
					onVolverAPreparar={handlers.onVolverAPreparar}
				/>
			)}
		</StyledComponent>
	);
};

const PanelCocineras = () => {
	const [pedidosHoy, setPedidosHoy] = useState([]);
	const [pedidosPosteriores, setPedidosPosteriores] = useState([]);
	const [pedidosRealizados, setPedidosRealizados] = useState([]);
	const [pedidosAtrasados, setPedidosAtrasados] = useState([]);
	const [isLoading, setIsLoading] = useState(true);
	const token = sessionStorage.getItem('token');
	const userId = useSelector((state) => state.usuario.userInfo.id);

	const fetchPedidos = async () => {
		setIsLoading(true);
		try {
			const data = await obtenerPedidosParaCocinera(userId, token);
			setPedidosHoy(data.pedidosHoy);
			setPedidosPosteriores(data.pedidosPosteriores);
			setPedidosRealizados(data.pedidosRealizados);
			setPedidosAtrasados(data.pedidosAtrasados);
		} catch (error) {
			alert('Hubo un problema al obtener los pedidos.');
		} finally {
			setIsLoading(false);
		}
	};

	const handleMarcarTerminado = async (pedidoId) => {
		try {
			await marcarPedidoComoTerminado(pedidoId, token);
			fetchPedidos();
		} catch (error) {
			alert('No se pudo marcar el pedido como terminado.');
		}
	};

	const handleReasignarPedido = async (pedidoId) => {
		try {
			await reasignarPedido(pedidoId, userId, token);
			fetchPedidos();
		} catch (error) {
			alert('No se pudo reasignar el pedido.');
		}
	};

	const handleVolverATrabajar = (pedidoId) => {
		actualizarPedidoCocinera(pedidoId, sessionStorage.getItem('token'))
			.then((data) => {
				toast.success(data.message);
				fetchPedidos();
			})
			.catch((e) => {
				toast.error(e.error);
			});
	};

	const productoValido = (detalle) => {
		return (
			!detalle.descripcion.includes('Mercado Pago') &&
			!detalle.descripcion.includes('Envío') &&
			!detalle.descripcion.includes('Cupon') &&
			!detalle.descripcion.includes('Retiro')
		);
	};

	const getPedidoConfig = (pedido, seccion) => {
		const isAssigned = pedido.cocinera_asignada === userId;
		const isUnassigned = !pedido.cocinera_asignada;

		// Configuración para pedidos realizados
		if (seccion === 'realizados') {
			return {
				StyledComponent: OtherCocineraItem,
				config: {
					showFechaEntrega: true,
					showCocineraInfo: true,
					buttons: ['volverAPreparar'],
				},
			};
		}

		// Configuración para pedidos atrasados
		if (seccion === 'atrasados') {
			if (isAssigned) {
				return {
					StyledComponent: AssignedPedidoItem,
					config: {
						showFechaEntrega: false,
						showCocineraInfo: true,
						buttons: ['terminar'],
					},
				};
			} else if (isUnassigned) {
				return {
					StyledComponent: UnassignedAtrasoItem,
					config: {
						showFechaEntrega: false,
						showCocineraInfo: true,
						buttons: ['asignar'],
					},
				};
			} else {
				return {
					StyledComponent: OtherCocineraItem,
					config: {
						showFechaEntrega: false,
						showCocineraInfo: true,
						buttons: ['reasignar'],
					},
				};
			}
		}

		// Configuración para pedidos de hoy
		if (seccion === 'hoy') {
			if (isAssigned) {
				return {
					StyledComponent: AssignedPedidoItem,
					config: {
						showFechaEntrega: false,
						showCocineraInfo: true,
						buttons: ['terminar'],
					},
				};
			} else if (isUnassigned) {
				return {
					StyledComponent: UnassignedNormalItem,
					config: {
						showFechaEntrega: false,
						showCocineraInfo: true,
						buttons: ['asignar'],
					},
				};
			} else {
				return {
					StyledComponent: OtherCocineraItem,
					config: {
						showFechaEntrega: false,
						showCocineraInfo: true,
						buttons: ['reasignar'],
					},
				};
			}
		}

		// Configuración para pedidos posteriores
		if (seccion === 'posteriores') {
			if (isAssigned) {
				return {
					StyledComponent: AssignedPedidoItem,
					config: {
						showFechaEntrega: true,
						showCocineraInfo: true,
						buttons: ['terminar'],
					},
				};
			} else if (isUnassigned) {
				return {
					StyledComponent: UnassignedNormalItem,
					config: {
						showFechaEntrega: true,
						showCocineraInfo: true,
						buttons: ['asignar'],
					},
				};
			} else {
				return {
					StyledComponent: OtherCocineraItem,
					config: {
						showFechaEntrega: true,
						showCocineraInfo: true,
						buttons: ['reasignar'],
					},
				};
			}
		}

		return null;
	};

	const handlers = {
		onMarcarTerminado: handleMarcarTerminado,
		onReasignar: handleReasignarPedido,
		onVolverAPreparar: handleVolverATrabajar,
	};

	useEffect(() => {
		fetchPedidos();
	}, []);

	return (
		<AnimationRevealPage>
			<Container>
				{isLoading ? (
					<p>Cargando pedidos...</p>
				) : (
					<>
						{pedidosAtrasados.length > 0 && (
							<>
								<SectionTitle>Pedidos atrasados</SectionTitle>
								{pedidosAtrasados.map((pedido) => {
									const pedidoConfig = getPedidoConfig(pedido, 'atrasados');
									return (
										<PedidoCard
											key={pedido.id}
											pedido={pedido}
											StyledComponent={pedidoConfig.StyledComponent}
											config={pedidoConfig.config}
											handlers={handlers}
											productoValido={productoValido}
										/>
									);
								})}
								<Divider />
							</>
						)}

						<SectionTitle>Pedidos para entregar hoy</SectionTitle>
						{pedidosHoy.length > 0 ? (
							pedidosHoy.map((pedido) => {
								const pedidoConfig = getPedidoConfig(pedido, 'hoy');
								return (
									<PedidoCard
										key={pedido.id}
										pedido={pedido}
										StyledComponent={pedidoConfig.StyledComponent}
										config={pedidoConfig.config}
										handlers={handlers}
										productoValido={productoValido}
									/>
								);
							})
						) : (
							<p>No hay pedidos para entregar hoy.</p>
						)}
						<Divider />

						<SectionTitle>Pedidos posteriores</SectionTitle>
						{pedidosPosteriores.length > 0 ? (
							pedidosPosteriores.map((pedido) => {
								const pedidoConfig = getPedidoConfig(pedido, 'posteriores');
								return (
									<PedidoCard
										key={pedido.id}
										pedido={pedido}
										StyledComponent={pedidoConfig.StyledComponent}
										config={pedidoConfig.config}
										handlers={handlers}
										productoValido={productoValido}
									/>
								);
							})
						) : (
							<p>No hay pedidos posteriores a hoy.</p>
						)}
						<Divider />

						<SectionTitle>Pedidos realizados</SectionTitle>
						{pedidosRealizados.length > 0 ? (
							pedidosRealizados.map((pedido) => {
								const pedidoConfig = getPedidoConfig(pedido, 'realizados');
								return (
									<PedidoCard
										key={pedido.id}
										pedido={pedido}
										StyledComponent={pedidoConfig.StyledComponent}
										config={pedidoConfig.config}
										handlers={handlers}
										productoValido={productoValido}
									/>
								);
							})
						) : (
							<p>No hay pedidos realizados.</p>
						)}
					</>
				)}
			</Container>
		</AnimationRevealPage>
	);
};

export default PanelCocineras;
